/* Autos Autorizados — buscar mientras se escribe, en la barrera.
 *
 * Guillermo (Portería, tickets 389 y 394, 13/10): «que cargando en el campo
 * patente, la misma ya aparezca y también la persona vinculada a esa patente,
 * casi como un auto completar. Y lo mismo en sentido inverso: cargando los
 * datos del personal en el campo nombre, debería aparecer la patente».
 *
 * Todo esto es puro (sin Supabase ni React) y trabaja sobre lo que la pantalla
 * ya tiene en la mano: la lista de autorizados vigentes, las entradas recientes
 * y quién está adentro. Buscar contra la base en cada tecla sería más lento en
 * el celular de la barrera y no sabría nada que la lista no sepa.
 *
 * ── LA PATENTE SE COMPARA SIN NADA QUE NO SEA LETRA O NÚMERO ──────────────
 *
 * En el celular se escribe «ab 123 cd», «AB-123-CD» o «ab123cd», y en la base
 * vive «AB123CD». Para buscar se pela todo lo que no es letra o número.
 *
 * ── EL NOMBRE SE BUSCA POR PEDAZOS, EN CUALQUIER ORDEN ────────────────────
 *
 * La lista guarda «SAAVEDRA, HECTOR». El de la barrera puede escribir «saav»,
 * «hector» o «hector saa»: cada pedazo tiene que estar en el nombre, sin
 * importar mayúsculas, tildes ni el orden. Primero van los que arrancan una
 * palabra con lo escrito, después los que lo tienen en el medio.
 */

import { hoyAr } from "./fechaAr";

/* «hace 5 min» / «hace 2 h»: cuánto hace que entró el que está adentro. */
export const haceCuanto = (iso, ahora = Date.now()) => {
  const min = Math.floor((ahora - new Date(iso).getTime()) / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d > 1 ? "s" : ""}`;
};

/* Patente para comparar: sólo letras y números, en mayúscula. */
export const patenteBusqueda = (p) =>
  String(p ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/* Texto para comparar nombres: minúscula, sin tildes, sin signos. */
export const textoBusqueda = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9ñ]+/g, " ")
    .trim();

/* Lo que se copia en «a quién viene» de una entrada: el sector si es del
 * personal, el motivo si es una autorización de afuera. */
export const detalleDe = (a) =>
  String(a?.sector ?? "").trim() || String(a?.motivo ?? "").trim();

/* ¿Es un auto del personal? Sólo lo que la base marcó así: una autorización de
 * afuera con sector no lo es. */
export const esPersonal = (a) => a?.personal === true;

/* Desde cuántas letras se busca. Con una sola, «A» trae media lista. */
export const MIN_LETRAS = 2;
export const TOPE_SUGERENCIAS = 6;

/* El registro de adentro de esa patente, si hay. */
export function adentroDe(patente, adentro) {
  const p = patenteBusqueda(patente);
  if (!p) return null;
  return (adentro || []).find((r) => patenteBusqueda(r.patente) === p) || null;
}

/* Las entradas recientes de autos que NO están en autorizados, una por
 * patente y la más nueva. Son los «faltan algunos» de Guillermo: autos que ya
 * entraron y que la pantalla puede ofrecer igual, avisando que no están en la
 * lista. `registros` puede venir en cualquier orden. */
export function recientesSinAutorizar(registros, autorizados) {
  const ya = new Set(
    (autorizados || []).map((a) => patenteBusqueda(a.patente)).filter(Boolean),
  );
  const porPatente = new Map();
  for (const r of registros || []) {
    const p = patenteBusqueda(r.patente);
    if (!p || ya.has(p)) continue;
    const previo = porPatente.get(p);
    if (!previo || String(r.entrada) > String(previo.entrada)) porPatente.set(p, r);
  }
  return [...porPatente.values()];
}

/* Una sugerencia, venga de la lista o de una entrada anterior. */
export function sugerenciaDeAutorizado(a, adentro) {
  return {
    clave: `a${a.id}`,
    tipo: "autorizado",
    patente: patenteBusqueda(a.patente),
    nombre: String(a.nombre ?? "").trim(),
    detalle: detalleDe(a),
    personal: esPersonal(a),
    autorizacion: a,
    adentro: adentroDe(a.patente, adentro),
  };
}

function deReciente(r, adentro) {
  return {
    clave: `r${r.id}`,
    tipo: "reciente",
    patente: patenteBusqueda(r.patente),
    nombre: String(r.nombre ?? "").trim(),
    detalle: String(r.a_quien ?? "").trim(),
    personal: false,
    registro: r,
    adentro: adentroDe(r.patente, adentro),
  };
}

const porNombre = (x, y) => x.nombre.localeCompare(y.nombre, "es");

/* Mientras se escribe la PATENTE: las que empiezan con lo escrito primero,
 * después las que lo tienen en el medio. Las autorizadas antes que las que
 * sólo entraron alguna vez. */
export function sugerirPorPatente(q, { autorizados = [], recientes = [], adentro = [] } = {},
                                  tope = TOPE_SUGERENCIAS) {
  const p = patenteBusqueda(q);
  if (p.length < MIN_LETRAS) return [];
  const rango = (patente) => (patente.startsWith(p) ? 0 : patente.includes(p) ? 1 : -1);
  const juntar = (filas, armar) =>
    filas
      .filter((f) => f.patente && rango(patenteBusqueda(f.patente)) >= 0)
      .map((f) => armar(f, adentro))
      .sort((x, y) => rango(x.patente) - rango(y.patente) || x.patente.localeCompare(y.patente));
  return [
    ...juntar(autorizados, sugerenciaDeAutorizado),
    ...juntar(recientes, deReciente),
  ].slice(0, tope);
}

/* ¿Cada pedazo de lo escrito está en el nombre? 0 = todos arrancan una
 * palabra, 1 = alguno está en el medio, -1 = no está. */
function rangoNombre(nombre, pedazos) {
  const n = textoBusqueda(nombre);
  if (!n) return -1;
  const palabras = n.split(" ");
  let rango = 0;
  for (const p of pedazos) {
    if (palabras.some((w) => w.startsWith(p))) continue;
    if (n.includes(p)) {
      rango = 1;
      continue;
    }
    return -1;
  }
  return rango;
}

/* Mientras se escribe el NOMBRE: la persona y su patente. */
export function sugerirPorNombre(q, { autorizados = [], recientes = [], adentro = [] } = {},
                                 tope = TOPE_SUGERENCIAS) {
  const t = textoBusqueda(q);
  if (t.replace(/\s/g, "").length < MIN_LETRAS) return [];
  const pedazos = t.split(" ").filter(Boolean);
  const juntar = (filas, armar) =>
    filas
      .map((f) => ({ f, r: rangoNombre(f.nombre, pedazos) }))
      .filter(({ r }) => r >= 0)
      .map(({ f, r }) => ({ s: armar(f, adentro), r }))
      .sort((x, y) => x.r - y.r || porNombre(x.s, y.s))
      .map(({ s }) => s);
  return [
    ...juntar(autorizados, sugerenciaDeAutorizado),
    ...juntar(recientes, deReciente),
  ].slice(0, tope);
}

/* La autorizada con EXACTAMENTE esa patente, si hay una sola. Es la que se
 * elige sola cuando se termina de escribir la patente sin tocar la lista. */
export function autorizadaExacta(patente, autorizados) {
  const p = patenteBusqueda(patente);
  if (p.length < MIN_LETRAS) return null;
  const hay = (autorizados || []).filter((a) => patenteBusqueda(a.patente) === p);
  return hay.length === 1 ? hay[0] : null;
}

/* Filtrar una lista (adentro, autorizados) por patente o nombre, con las
 * mismas reglas. Vacío devuelve todo. */
export function filtrarPorPatenteONombre(filas, q) {
  const t = textoBusqueda(q);
  if (!t) return filas || [];
  const p = patenteBusqueda(q);
  const pedazos = t.split(" ").filter(Boolean);
  return (filas || []).filter(
    (f) =>
      (p.length >= MIN_LETRAS && patenteBusqueda(f.patente).includes(p)) ||
      rangoNombre(f.nombre, pedazos) >= 0,
  );
}

/* ¿Esa patente tiene una autorización DE AFUERA que ya no vale (vencida o dada
 * de baja)? Se mira en la lista de no vigentes que la pantalla ya tiene.
 *
 * Es la que Nico dio por un día a un proveedor: si vuelve, se lo registra como
 * a cualquiera, pero no se le ofrece «Sumar a autorizados como fijo» — eso la
 * volvería una autorización sin vencimiento. La renueva quien autoriza. (La
 * base lo rebota igual; esto es para no ofrecer un botón que no corresponde.)
 *
 * `noVigentes` ya viene filtrada como no vigente (`autorizadosNoVigentes`). */
export function afueraNoVigente(patente, noVigentes) {
  const p = patenteBusqueda(patente);
  if (!p) return null;
  return (
    (noVigentes || []).find((a) => !esPersonal(a) && patenteBusqueda(a.patente) === p) || null
  );
}

/* ── UN SOLO LISTADO (Nico, ticket 441, 19/10) ─────────────────────────────
 *
 * «Autorización de autos se ve engorrosamente difícil. ¿No puede haber un solo
 * listado y un solo lugar para agregar gente? En ese listado que se distingan
 * los temporales o los dados de baja con otro color o algo distinto».
 *
 * Había tres listas (autos del personal, autorizados de antemano y, plegadas,
 * las no vigentes) y dos formularios. Ahora es una lista, y lo que distingue a
 * cada renglón es CÓMO VALE, que es lo que él mira:
 *
 *   fijo      vigente y sin vencimiento (los autos de la casa, casi todos).
 *   temporal  vigente hasta una fecha (`vence` incluye su día).
 *   vencido   se le pasó la fecha: ya no entra, pero sigue a la vista.
 *   baja      alguien lo sacó a mano (`activo = false`). La baja gana sobre
 *             el vencimiento, como en `estadoAutorizacion`.
 *
 * Es la misma regla que filtra la base (`autorizados()` / `quienEsPatente()`),
 * para una fila que ya se tiene en la mano. Una fila de las vigentes no trae
 * `activo`: sin el dato, está activa. */
export function claseAutorizado(a, hoy = hoyAr()) {
  if (!a) return null;
  if (a.activo === false) return "baja";
  const vence = a.vence ? String(a.vence).slice(0, 10) : "";
  if (!vence) return "fijo";
  return vence < hoy ? "vencido" : "temporal";
}

export const esVigente = (clase) => clase === "fijo" || clase === "temporal";

/* Los filtros de arriba de la lista. «Temporales» incluye los vencidos: son
 * temporales a los que se les pasó la fecha. */
export const VISTAS = [
  { id: "todos", label: "Todos", clases: ["fijo", "temporal", "vencido", "baja"] },
  { id: "vigentes", label: "Vigentes", clases: ["fijo", "temporal"] },
  { id: "temporales", label: "Temporales", clases: ["temporal", "vencido"] },
  { id: "bajas", label: "Bajas", clases: ["baja"] },
];

export const enVista = (vista, clase) =>
  (VISTAS.find((v) => v.id === vista) || VISTAS[0]).clases.includes(clase);

/* Cuántos hay en cada filtro. */
export function contarPorVista(filas, hoy = hoyAr()) {
  const clases = (filas || []).map((a) => claseAutorizado(a, hoy));
  return Object.fromEntries(
    VISTAS.map((v) => [v.id, clases.filter((c) => v.clases.includes(c)).length]),
  );
}

/* El orden de la lista: primero los que entran (fijos y temporales juntos, por
 * nombre: es como se los busca), después los vencidos (el último que venció
 * arriba) y al final los dados de baja (la última baja arriba). */
const GRUPO = { fijo: 0, temporal: 0, vencido: 1, baja: 2 };
const cuandoDejo = (a, clase) =>
  clase === "vencido"
    ? String(a.vence ?? "")
    : clase === "baja"
      ? String(a.baja_en ?? a.desde ?? "")
      : "";
const nombreParaOrdenar = (a) => String(a.nombre ?? "").trim() || patenteBusqueda(a.patente);

export function ordenarAutorizados(filas, hoy = hoyAr()) {
  return (filas || [])
    .map((a) => ({ a, c: claseAutorizado(a, hoy) }))
    .sort(
      (x, y) =>
        GRUPO[x.c] - GRUPO[y.c] ||
        cuandoDejo(y.a, y.c).localeCompare(cuandoDejo(x.a, x.c)) ||
        nombreParaOrdenar(x.a).localeCompare(nombreParaOrdenar(y.a), "es"),
    )
    .map(({ a }) => a);
}

/* ¿Hay OTRA autorización con esa patente en estas filas? Se compara sólo por
 * letras y números: «KJQ-439» y «KJQ439» son la misma chapa. */
export function otraConPatente(filas, patente, id = null) {
  const p = patenteBusqueda(patente);
  if (!p) return null;
  return (
    (filas || []).find(
      (a) => patenteBusqueda(a.patente) === p && String(a.id) !== String(id),
    ) || null
  );
}

/* ── Cuando algo no se guarda ──────────────────────────────────────────────
 *
 * Sin señal, el error que llega es «TypeError: Failed to fetch»: en la barrera
 * eso no dice que la entrada NO quedó anotada. Adelante va siempre un texto
 * propio —qué no se pudo y qué hacer— y, si es por la conexión, se dice. Los
 * errores que ya traen un texto pensado para la pantalla (`paraMostrar`) salen
 * tal cual. El detalle técnico queda en la consola, para quien lo revise. */
const SIN_CONEXION =
  /failed to fetch|networkerror|network request failed|load failed|fetch failed|err_internet|err_network|timed? ?out/i;

export const esSinConexion = (e) =>
  SIN_CONEXION.test(`${e?.message ?? e ?? ""} ${e?.details ?? ""}`) ||
  (typeof navigator !== "undefined" && navigator.onLine === false);

export function avisoDeError(e, queNoSePudo, queHacer = "Probá de nuevo.") {
  if (e?.paraMostrar && e.message) return e.message;
  if (typeof console !== "undefined") console.warn(queNoSePudo, e);
  return `${queNoSePudo}${esSinConexion(e) ? " (sin conexión)" : ""}. ${queHacer}`;
}

/* Lo que le falta a una autorización y la barrera acaba de anotar (ticket
 * 521): la patente y/o el DNI, SÓLO si en la autorización están vacíos. Lo
 * que cargó quien autorizó no se pisa. `{}` es «no hay nada que completar». */
export function faltaCompletar(a, { patente, dni } = {}) {
  if (!a) return {};
  const vacio = (v) => !String(v ?? "").trim();
  const p = patenteBusqueda(patente);
  const d = String(dni ?? "").trim();
  return {
    ...(vacio(a.patente) && p ? { patente: p } : {}),
    ...(vacio(a.dni) && d ? { dni: d } : {}),
  };
}
